import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { authStorage, authLog } from '@practium/features/auth';
import { User } from '@practium/shared';
import { toApiError, getApiErrorMessage, getApiUrl } from '../utils';
import { createConsentError, isConsentRequired } from './consentCheck';
import { isAnonymizationPending, toAnonymizationPendingError } from './anonymizationCheck';

interface GoogleAuthApiResponse {
	csrfToken: string;
	accessToken: string;
}

export const useGoogleLogin = () => {
	return useMutation({
		mutationFn: async (code: string) => {
			const apiUrl = getApiUrl();

			const { data } = await axios
				.post<GoogleAuthApiResponse>(
					`${apiUrl}auth/google`,
					{ code, redirectUri: 'postmessage' },
					{ withCredentials: true }
				)
				.catch((error) => {
					const anonymizationError = toAnonymizationPendingError(error);
					if (anonymizationError) throw anonymizationError;
					throw toApiError(error, 'Erro ao autenticar com o Google');
				});

			const cleanToken = data.accessToken.replace(/^bearer\s+/i, '');

			if (data.csrfToken) {
				authStorage.saveCsrf(data.csrfToken);
			}

			const { data: user } = await axios
				.get<User>(`${apiUrl}user/get`, {
					headers: { Authorization: `bearer ${cleanToken}` },
				})
				.catch((error) => {
					if (isConsentRequired(error)) throw error;
					if (axios.isAxiosError(error) && error.response?.status === 428) {
						throw createConsentError(cleanToken);
					}
					throw toApiError(error, 'Erro ao autenticar com o Google');
				});

			return { user, token: cleanToken };
		},
		onSuccess: ({ user, token }) => {
			authStorage.saveSession(user, token, true);
			authLog.logLogin(user._id, user.email, true);
		},
		onError: (error) => {
			if (isConsentRequired(error) || isAnonymizationPending(error)) return;
			console.error(
				'Erro ao autenticar com Google:',
				getApiErrorMessage(error, 'Erro ao autenticar com o Google')
			);
		},
	});
};
